import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import Button from './Button'
import reportError from '@utils/errorReporter'
import logger from '@utils/logger'
import {
  isModuleLoadError,
  recoverFromModuleLoadError
} from '@utils/moduleLoadRecovery'

/**
 * Error Boundary Component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    })

    // Log to console in dev, report to server in production
    if (import.meta.env.DEV) {
      logger.error('Error Boundary caught an error:', error, errorInfo)
    }
    reportError({
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack
    })

    recoverFromModuleLoadError(error)
  }

  handleRetry = () => {
    if (!this.props.compact || isModuleLoadError(this.state.error)) {
      window.location.reload()
      return
    }

    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.compact) {
        return (
          <div className="p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400">
              Component failed to load
            </p>
            <button
              onClick={this.handleRetry}
              className="text-xs text-red-500 underline mt-1"
            >
              Retry
            </button>
          </div>
        )
      }
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full mx-auto p-6">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-error-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Something went wrong
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                We&apos;re sorry! An unexpected error occurred. Please try
                refreshing the page or contact support if the problem persists.
              </p>

              <div className="space-y-4">
                <Button onClick={this.handleRetry} variant="primary" fullWidth>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>

                <Button
                  onClick={() => (window.location.href = 'https://x3algo.com')}
                  variant="outline"
                  fullWidth
                >
                  Go to Homepage
                </Button>
              </div>

              {import.meta.env && import.meta.env.DEV && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    Error Details (Development)
                  </summary>
                  <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs text-gray-700 dark:text-gray-300 overflow-auto">
                    <div className="font-bold mb-2">Error:</div>
                    <pre className="whitespace-pre-wrap">
                      {this.state.error && this.state.error.toString()}
                    </pre>
                    <div className="font-bold mt-4 mb-2">Stack Trace:</div>
                    <pre className="whitespace-pre-wrap">
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
